import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconWand,
  IconSearch,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import api from "../../services/api";
import type { Ingredient } from "../types";

interface LinkedIngredient extends Ingredient {
  MenuItemIngredient?: { confidence: number | null; reasoning: string | null; display_override?: boolean | null };
}

interface Props {
  menuItemId: string;
}

const CATEGORY_OPTIONS = [
  "fish","shellfish","seafood","meat","poultry","protein","vegetable","mushroom",
  "grain","noodle","tofu","seaweed","egg","dairy","sauce","seasoning","broth",
  "fruit","oil","garnish","drink","component","other",
].map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));

const RARITY_OPTIONS = [
  { value: "common", label: "Common" },
  { value: "uncommon", label: "Uncommon" },
  { value: "rare", label: "Rare" },
  { value: "seasonal", label: "Seasonal" },
];

const RARITY_COLORS: Record<string, string> = {
  common: "gray",
  uncommon: "green",
  rare: "orange",
  seasonal: "blue",
};

const emptyForm = () => ({
  canonical_name: "",
  name_en: "",
  name_jp: "",
  category: "" as string,
  rarity: "common" as string,
  description_en: "",
  description_jp: "",
  display: false,
});

export const MenuItemIngredientsV2: React.FC<Props> = ({ menuItemId }) => {
  const [linked, setLinked] = useState<LinkedIngredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<"search" | "create">("search");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Ingredient[]>([]);
  const [searching, setSearching] = useState(false);

  // Create form state
  const [form, setForm] = useState(emptyForm());
  const [generatingFields, setGeneratingFields] = useState(false);
  const [linking, setLinking] = useState(false);

  const fetchLinked = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ ingredients: LinkedIngredient[] }>(
        `/menu-items/${menuItemId}/ingredients`
      );
      setLinked(data.ingredients ?? []);
    } catch {
      notifications.show({ color: "red", message: "Failed to load ingredients" });
    } finally {
      setLoading(false);
    }
  }, [menuItemId]);

  useEffect(() => { fetchLinked(); }, [fetchLinked]);

  const handleSearch = useCallback(async (q: string) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get<{ data: Ingredient[] }>(
        `/ingredients?search=${encodeURIComponent(q)}&pageSize=10`
      );
      setSearchResults(data.data ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleLinkExisting = useCallback(async (ingredient: Ingredient) => {
    setLinking(true);
    try {
      await api.post(`/menu-items/${menuItemId}/ingredients`, { ingredientId: ingredient.id });
      await fetchLinked();
      setModalOpen(false);
      setSearchQuery("");
      setSearchResults([]);
    } catch (err: any) {
      notifications.show({ color: "red", message: err?.response?.data?.error ?? "Failed to link ingredient" });
    } finally {
      setLinking(false);
    }
  }, [menuItemId, fetchLinked]);

  const handleGenerateFields = useCallback(async () => {
    if (!form.canonical_name.trim()) {
      notifications.show({ color: "yellow", message: "Enter a canonical name first" });
      return;
    }
    setGeneratingFields(true);
    try {
      const { data } = await api.post<{ name_en: string; name_jp: string; description_en: string; description_jp: string }>(
        "/menu-items/generate-ingredient-fields",
        {
          canonical_name: form.canonical_name,
          category: form.category || undefined,
          rarity: form.rarity || undefined,
          name_en: form.name_en || undefined,
          name_jp: form.name_jp || undefined,
          description_en: form.description_en || undefined,
          description_jp: form.description_jp || undefined,
        }
      );
      setForm((prev) => ({
        ...prev,
        name_en: data.name_en || prev.name_en,
        name_jp: data.name_jp || prev.name_jp,
        description_en: data.description_en || prev.description_en,
        description_jp: data.description_jp || prev.description_jp,
      }));
    } catch {
      notifications.show({ color: "red", message: "Failed to generate fields" });
    } finally {
      setGeneratingFields(false);
    }
  }, [form]);

  const handleCreate = useCallback(async () => {
    if (!form.canonical_name.trim()) {
      notifications.show({ color: "yellow", message: "Canonical name is required" });
      return;
    }
    setLinking(true);
    try {
      await api.post(`/menu-items/${menuItemId}/ingredients`, {
        canonical_name: form.canonical_name,
        name_en: form.name_en || undefined,
        name_jp: form.name_jp || undefined,
        category: form.category || undefined,
        rarity: form.rarity || undefined,
        description_en: form.description_en || undefined,
        description_jp: form.description_jp || undefined,
        display: form.display,
      });
      await fetchLinked();
      setModalOpen(false);
      setForm(emptyForm());
    } catch (err: any) {
      notifications.show({ color: "red", message: err?.response?.data?.error ?? "Failed to create ingredient" });
    } finally {
      setLinking(false);
    }
  }, [form, menuItemId, fetchLinked]);

  const handleUpdateOverride = useCallback(async (ingredientId: string, value: string | null) => {
    const display_override = value === "show" ? true : value === "hide" ? false : null;
    try {
      await api.patch(`/menu-items/${menuItemId}/ingredients/${ingredientId}`, { display_override });
      setLinked((prev) => prev.map((i) =>
        i.id === ingredientId
          ? { ...i, MenuItemIngredient: { ...i.MenuItemIngredient, display_override } as any }
          : i
      ));
    } catch {
      notifications.show({ color: "red", message: "Failed to update display override" });
    }
  }, [menuItemId]);

  const handleUnlink = useCallback(async (ingredientId: string) => {
    try {
      await api.delete(`/menu-items/${menuItemId}/ingredients/${ingredientId}`);
      setLinked((prev) => prev.filter((i) => i.id !== ingredientId));
    } catch {
      notifications.show({ color: "red", message: "Failed to remove ingredient" });
    }
  }, [menuItemId]);

  const openCreate = () => {
    setMode("create");
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openSearch = () => {
    setMode("search");
    setSearchQuery("");
    setSearchResults([]);
    setModalOpen(true);
  };

  return (
    <>
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={5}>Ingredients (Linked)</Title>
          <Group gap="xs">
            <Button size="xs" variant="light" leftSection={<IconSearch size={13} />} onClick={openSearch}>
              Link existing
            </Button>
            <Button size="xs" variant="light" leftSection={<IconPlus size={13} />} onClick={openCreate}>
              Add new
            </Button>
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          Linked to the centralized Ingredient model via the junction table.
        </Text>

        {loading ? (
          <Loader size="sm" />
        ) : linked.length === 0 ? (
          <Text size="sm" c="dimmed">No ingredients linked yet.</Text>
        ) : (
          <Stack gap="xs">
            {linked.map((ing) => {
              const overrideVal = ing.MenuItemIngredient?.display_override;
              const selectValue = overrideVal === true ? "show" : overrideVal === false ? "hide" : "default";
              return (
                <Group key={ing.id} justify="space-between" wrap="nowrap" p="xs"
                  style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>{ing.name_en || ing.canonical_name}</Text>
                      {ing.name_jp && <Text size="xs" c="dimmed">{ing.name_jp}</Text>}
                      {ing.rarity && (
                        <Badge size="xs" color={RARITY_COLORS[ing.rarity] ?? "gray"}>{ing.rarity}</Badge>
                      )}
                      {ing.display && overrideVal !== false && <Badge size="xs" color="violet">displayed</Badge>}
                      {overrideVal === true && <Badge size="xs" color="teal">show (override)</Badge>}
                      {overrideVal === false && <Badge size="xs" color="orange">hidden (override)</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {ing.canonical_name}{ing.category ? ` · ${ing.category}` : ""}
                    </Text>
                    {ing.description_en && (
                      <Text size="xs" c="dimmed" lineClamp={2}>{ing.description_en}</Text>
                    )}
                  </Stack>
                  <Group gap="xs" wrap="nowrap">
                    <Select
                      size="xs"
                      w={130}
                      value={selectValue}
                      onChange={(v) => handleUpdateOverride(ing.id, v)}
                      data={[
                        { value: "default", label: `Default (${ing.display ? "show" : "hide"})` },
                        { value: "show", label: "Always show" },
                        { value: "hide", label: "Always hide" },
                      ]}
                      allowDeselect={false}
                    />
                    <ActionIcon color="red" variant="subtle" size="sm" onClick={() => handleUnlink(ing.id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={mode === "search" ? "Link existing ingredient" : "Add new ingredient"}
        size="lg"
      >
        {mode === "search" ? (
          <Stack gap="md">
            <TextInput
              placeholder="Search by name or canonical name…"
              value={searchQuery}
              onChange={(e) => handleSearch(e.currentTarget.value)}
              rightSection={searching ? <Loader size="xs" /> : null}
              autoFocus
            />
            {searchResults.length === 0 && searchQuery.trim() && !searching && (
              <Text size="sm" c="dimmed">No results. Try a different term or add a new ingredient.</Text>
            )}
            <Stack gap="xs">
              {searchResults.map((ing) => {
                const alreadyLinked = linked.some((l) => l.id === ing.id);
                return (
                  <Group key={ing.id} justify="space-between" p="xs"
                    style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
                    <Stack gap={2}>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>{ing.name_en || ing.canonical_name}</Text>
                        {ing.rarity && <Badge size="xs" color={RARITY_COLORS[ing.rarity] ?? "gray"}>{ing.rarity}</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed">{ing.canonical_name}{ing.category ? ` · ${ing.category}` : ""}</Text>
                    </Stack>
                    <Button
                      size="xs"
                      variant="light"
                      disabled={alreadyLinked || linking}
                      onClick={() => handleLinkExisting(ing)}
                    >
                      {alreadyLinked ? "Linked" : "Link"}
                    </Button>
                  </Group>
                );
              })}
            </Stack>
            <Divider label="or" labelPosition="center" />
            <Button variant="subtle" size="xs" onClick={() => { setMode("create"); setForm({ ...emptyForm(), canonical_name: searchQuery }); }}>
              Create new ingredient instead
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Group align="flex-end" wrap="nowrap">
              <TextInput
                label="Canonical name"
                required
                style={{ flex: 1 }}
                value={form.canonical_name}
                onChange={(e) => setForm((p) => ({ ...p, canonical_name: e.currentTarget.value }))}
                placeholder="e.g. wagyu, maguro, shiitake"
              />
              <Button
                variant="light"
                leftSection={<IconWand size={14} />}
                loading={generatingFields}
                onClick={handleGenerateFields}
                disabled={!form.canonical_name.trim()}
              >
                Generate fields
              </Button>
            </Group>

            <Group grow>
              <TextInput
                label="Name (EN)"
                value={form.name_en}
                onChange={(e) => setForm((p) => ({ ...p, name_en: e.currentTarget.value }))}
                placeholder="English name"
              />
              <TextInput
                label="Name (JP)"
                value={form.name_jp}
                onChange={(e) => setForm((p) => ({ ...p, name_jp: e.currentTarget.value }))}
                placeholder="Japanese name"
              />
            </Group>

            <Group grow>
              <Select
                label="Category"
                data={CATEGORY_OPTIONS}
                value={form.category || null}
                onChange={(v) => setForm((p) => ({ ...p, category: v ?? "" }))}
                clearable
                searchable
              />
              <Select
                label="Rarity"
                data={RARITY_OPTIONS}
                value={form.rarity}
                onChange={(v) => setForm((p) => ({ ...p, rarity: v ?? "common" }))}
              />
            </Group>

            <TextInput
              label="Description (EN)"
              value={form.description_en}
              onChange={(e) => setForm((p) => ({ ...p, description_en: e.currentTarget.value }))}
              placeholder="One sentence about the ingredient"
            />

            <Textarea
              label="Description (JP)"
              value={form.description_jp}
              onChange={(e) => setForm((p) => ({ ...p, description_jp: e.currentTarget.value }))}
              minRows={2}
              placeholder="Japanese description"
            />

            <Switch
              label="Display on menu"
              checked={form.display}
              onChange={(e) => setForm((p) => ({ ...p, display: e.currentTarget.checked }))}
            />

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} loading={linking} disabled={!form.canonical_name.trim()}>
                Add ingredient
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
};
